import Phaser from 'phaser';
import { Controls } from '../input/Controls';
import { Player } from '../entities/Player';
import { Vehicle } from '../entities/Vehicle';
import { Pedestrian } from '../entities/Pedestrian';
import { CopOnFoot } from '../entities/Police';
import { LiverpoolMap } from '../world/LiverpoolMap';
import { Ambient } from '../audio/Ambient';
import { Sfx } from '../audio/Sfx';
import { LANDMARKS, landmark } from '../world/landmarks';
import { WantedSystem } from '../systems/WantedSystem';
import { MissionSystem } from '../systems/MissionSystem';
import { PLAYER, WANTED, TILE } from '../config';
import type { VehicleKind } from '../types';

export class GameScene extends Phaser.Scene {
  private map!: LiverpoolMap;
  private controls!: Controls;
  private player!: Player;
  private vehicles: Vehicle[] = [];
  private copCars: Vehicle[] = [];
  private peds: Pedestrian[] = [];
  private cops: CopOnFoot[] = [];
  private wanted = new WantedSystem();
  private missions!: MissionSystem;
  private rng = new Phaser.Math.RandomDataGenerator([Date.now().toString()]);
  private vehicleGroup!: Phaser.Physics.Arcade.Group;
  private pedGroup!: Phaser.Physics.Arcade.Group;
  private copGroup!: Phaser.Physics.Arcade.Group;
  private solids!: Phaser.Physics.Arcade.StaticGroup;
  private lastCopSpawn = 0;
  private lastAreaName = '';
  private missionMarker: { x: number; y: number } | null = null;

  constructor() { super('GameScene'); }

  create() {
    this.map = new LiverpoolMap();
    const bounds = this.map.bounds();
    this.physics.world.setBounds(0, 0, bounds.width, bounds.height);
    this.cameras.main.setBounds(0, 0, bounds.width, bounds.height);

    this.solids = this.map.build(this);

    // Landmark labels
    for (const lm of LANDMARKS) {
      this.add.text(lm.x, lm.y, lm.name, {
        fontSize: '11px', color: '#fff',
        backgroundColor: 'rgba(0,0,0,0.55)', padding: { left: 3, right: 3, top: 1, bottom: 1 }
      }).setOrigin(0.5).setDepth(20);
    }

    // Spawn player at Lime Street (St Georges Hall) — well away from the parcel pickup.
    const lime = landmark('Lime Street');
    const spawn = this.map.nearestWalkable(lime.x, lime.y);
    this.player = new Player(this, spawn.x, spawn.y);
    this.cameras.main.startFollow(this.player.sprite, true, 0.12, 0.12);
    this.cameras.main.setZoom(1);

    this.controls = new Controls(this);
    this.missions = new MissionSystem(this, this.player, this.wanted);

    // Groups
    this.vehicleGroup = this.physics.add.group();
    this.pedGroup = this.physics.add.group();
    this.copGroup = this.physics.add.group();

    // Spawn some vehicles around the city
    this.spawnVehicles(20);
    // Spawn pedestrians on pavement
    this.spawnPeds(40);

    // Collisions
    this.physics.add.collider(this.player.sprite, this.solids);
    this.physics.add.collider(this.vehicleGroup, this.solids, (v) => {
      const veh = (v as Phaser.Physics.Arcade.Sprite).getData('vehicle') as Vehicle | undefined;
      if (veh) veh.damage(2);
    });
    this.physics.add.collider(this.vehicleGroup, this.vehicleGroup);
    this.physics.add.collider(this.pedGroup, this.solids);
    this.physics.add.collider(this.copGroup, this.solids);

    // Vehicle vs ped → run over
    this.physics.add.overlap(this.vehicleGroup, this.pedGroup, (v, p) => {
      const veh = (v as Phaser.Physics.Arcade.Sprite).getData('vehicle') as Vehicle | undefined;
      const ped = (p as Phaser.Physics.Arcade.Sprite).getData('ped') as Pedestrian | undefined;
      if (!veh || !ped || veh.exploded) return;
      const speed = (veh.sprite.body!.velocity as Phaser.Math.Vector2).length();
      if (speed < 30) return;
      const dmg = Math.min(80, speed * 0.3);
      ped.damage(dmg, veh.sprite.x, veh.sprite.y);
      if (veh.driver === 'player') {
        this.wanted.bump(ped.alive ? WANTED.hitPed : WANTED.killPed);
      }
    });

    // Vehicle vs cop on foot
    this.physics.add.overlap(this.vehicleGroup, this.copGroup, (v, c) => {
      const veh = (v as Phaser.Physics.Arcade.Sprite).getData('vehicle') as Vehicle | undefined;
      const cop = (c as Phaser.Physics.Arcade.Sprite).getData('cop') as CopOnFoot | undefined;
      if (!veh || !cop || veh.exploded) return;
      const speed = (veh.sprite.body!.velocity as Phaser.Math.Vector2).length();
      if (speed < 30) return;
      cop.damage(50);
      if (veh.driver === 'player') this.wanted.bump(WANTED.hitCop);
    });

    // Cop on foot tackles player on foot
    this.physics.add.overlap(this.player.sprite, this.copGroup, (_pl, c) => {
      if (this.player.mode !== 'OnFoot') return;
      const cop = (c as Phaser.Physics.Arcade.Sprite).getData('cop') as CopOnFoot | undefined;
      if (!cop || !cop.alive) return;
      this.player.damage(0.5);
      this.events.emit('hud:hp', this.player.hp, PLAYER.health);
      if (this.player.hp <= 0) this.busted();
    });

    // HUD wiring
    this.wanted.on('change', (n: number) => this.events.emit('hud:stars', n));
    this.missions.on('view', (m: any) => {
      this.missionMarker = m.marker ?? null;
      this.events.emit('hud:mission', m);
    });
    this.events.emit('hud:cash', this.player.cash);
    this.events.emit('hud:hp', this.player.hp, PLAYER.health);
    this.events.emit('hud:stars', this.wanted.level);

    // Kick off mission 1
    this.time.delayedCall(500, () => this.missions.start('ScouseDelivery'));

    // Ambient soundscape — drizzle, low city hum, occasional gull/honk
    Ambient.start();
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => Ambient.stop());

    // Pause overlay
    this.input.keyboard!.on('keydown-ESC', () => {
      this.scene.pause();
      this.scene.launch('PauseScene');
    });
  }

  update(time: number, deltaMs: number) {
    const dt = deltaMs / 1000;
    this.controls.update();
    const c = this.controls.state;

    // Player input
    this.player.update(c, dt);

    // Enter/exit vehicle
    if (c.enter) this.tryEnterOrExit();

    // Punch
    if (c.punch && this.player.mode === 'OnFoot' && this.player.canPunch(time)) {
      this.doPunch(time);
    }

    // Update entities
    const pp = this.player.pos();
    for (const ped of this.peds) ped.update(time, pp.x, pp.y);
    for (const cop of this.cops) cop.update(this.player);
    for (const cv of this.copCars) {
      if (cv.exploded) continue;
      const pp = this.player.pos();
      const dx = pp.x - cv.sprite.x, dy = pp.y - cv.sprite.y;
      const targetAng = Math.atan2(dx, -dy);
      const diff = Phaser.Math.Angle.Wrap(targetAng - cv.sprite.rotation);
      const steer = Phaser.Math.Clamp(diff, -1, 1);
      cv.drive(0.8, steer, dt);
    }

    // Wanted decay
    this.wanted.update(time, dt);

    // Cop sighting check (any cop within range = no decay)
    if (this.cops.some(cop => cop.alive && Phaser.Math.Distance.Between(
      cop.sprite.x, cop.sprite.y, this.player.pos().x, this.player.pos().y) < WANTED.copSightRange)) {
      this.wanted.noteSighting(time);
    }

    // Spawn cops based on wanted level
    this.spawnCopsIfNeeded(time);

    // Camera zoom
    const targetZoom = this.player.mode === 'InVehicle' ? 0.85 : 1.1;
    const cam = this.cameras.main;
    cam.setZoom(Phaser.Math.Linear(cam.zoom, targetZoom, 0.05));

    // Missions
    this.missions.update(time);
    this.updateMissionArrow();

    // Cleanup dead peds / cops / exploded vehicles
    this.peds = this.peds.filter(p => p.alive);
    this.cops = this.cops.filter(c => c.alive);
    this.copCars = this.copCars.filter(v => !v.exploded);

    // HUD area updates
    const p = this.player.pos();
    const area = this.map.areaAt(p.x, p.y);
    if (area !== this.lastAreaName) {
      this.lastAreaName = area;
      this.events.emit('hud:area', area);
    }

    this.events.emit('hud:cash', this.player.cash);
    this.events.emit('hud:hp', this.player.hp, PLAYER.health);
  }

  private tryEnterOrExit() {
    if (this.player.mode === 'InVehicle') {
      this.player.exitVehicle();
      return;
    }
    // Find nearest non-exploded vehicle within range
    const p = this.player.pos();
    let nearest: Vehicle | null = null;
    let bestD = PLAYER.enterRange;
    for (const v of this.vehicles) {
      if (v.exploded) continue;
      const d = Phaser.Math.Distance.Between(p.x, p.y, v.sprite.x, v.sprite.y);
      if (d < bestD) { bestD = d; nearest = v; }
    }
    if (nearest) {
      const wasOccupied = nearest.driver === 'ai';
      this.player.enterVehicle(nearest);
      if (wasOccupied) this.wanted.bump(WANTED.stealOccupied);
    }
  }

  private doPunch(now: number) {
    this.player.registerPunch(now);
    Sfx.punch();
    const p = this.player.pos();
    const fx = Math.sin(this.player.facing);
    const fy = -Math.cos(this.player.facing);
    const tx = p.x + fx * PLAYER.punchRange;
    const ty = p.y + fy * PLAYER.punchRange;
    const fxObj = this.add.circle(tx, ty, 6, 0xffffff, 0.8);
    this.tweens.add({ targets: fxObj, alpha: 0, scale: 2, duration: 180, onComplete: () => fxObj.destroy() });

    let hitSomething = false;
    for (const ped of this.peds) {
      if (!ped.alive) continue;
      if (Phaser.Math.Distance.Between(tx, ty, ped.sprite.x, ped.sprite.y) < 16) {
        ped.damage(PLAYER.punchDamage, p.x, p.y);
        this.wanted.bump(ped.alive ? WANTED.hitPed : WANTED.killPed);
        hitSomething = true;
        break;
      }
    }
    if (!hitSomething) {
      for (const cop of this.cops) {
        if (!cop.alive) continue;
        if (Phaser.Math.Distance.Between(tx, ty, cop.sprite.x, cop.sprite.y) < 16) {
          cop.damage(PLAYER.punchDamage);
          this.wanted.bump(WANTED.hitCop);
          break;
        }
      }
    }
  }

  private spawnVehicles(n: number) {
    const kinds: VehicleKind[] = ['Hatchback', 'TaxiBlack'];
    for (let i = 0; i < n; i++) {
      const pos = this.map.randomRoad(this.rng);
      const k = kinds[this.rng.between(0, kinds.length - 1)];
      const v = new Vehicle(this, pos.x, pos.y, k);
      v.sprite.rotation = this.rng.between(0, 3) * Math.PI / 2;
      v.driver = 'none';
      this.vehicles.push(v);
      this.vehicleGroup.add(v.sprite);
    }
  }

  private spawnPeds(n: number) {
    for (let i = 0; i < n; i++) {
      const pos = this.map.randomPavement(this.rng);
      const ped = new Pedestrian(this, pos.x, pos.y, this.map);
      this.peds.push(ped);
      this.pedGroup.add(ped.sprite);
    }
  }

  private spawnCopsIfNeeded(now: number) {
    const stars = this.wanted.level;
    if (stars <= 0) return;
    const targetCount = stars * 2;
    if (this.cops.length >= targetCount) return;
    const cooldown = Math.max(800, 3000 - stars * 400);
    if (now - this.lastCopSpawn < cooldown) return;
    this.lastCopSpawn = now;

    // spawn at edge of camera view
    const cam = this.cameras.main;
    const margin = 80;
    const p = this.player.pos();
    const ang = Math.random() * Math.PI * 2;
    const r = Math.max(cam.width, cam.height) / 2 / cam.zoom + margin;
    const x = Phaser.Math.Clamp(p.x + Math.cos(ang) * r, TILE * 2, this.map.bounds().width - TILE * 2);
    const y = Phaser.Math.Clamp(p.y + Math.sin(ang) * r, TILE * 2, this.map.bounds().height - TILE * 2);

    if (stars >= 2 && Math.random() < 0.5) {
      const v = new Vehicle(this, x, y, 'PoliceCar');
      v.driver = 'ai';
      this.vehicles.push(v);
      this.copCars.push(v);
      this.vehicleGroup.add(v.sprite);
    } else {
      const cop = new CopOnFoot(this, x, y);
      this.cops.push(cop);
      this.copGroup.add(cop.sprite);
    }
  }

  private updateMissionArrow() {
    const m = this.missionMarker;
    const cam = this.cameras.main;
    const onScreen = m ? cam.worldView.contains(m.x, m.y) : true;
    if (!m || onScreen) {
      this.events.emit('hud:arrow', null);
      return;
    }
    const p = this.player.pos();
    this.events.emit('hud:arrow', {
      angle: Math.atan2(m.y - p.y, m.x - p.x),
      distance: Phaser.Math.Distance.Between(p.x, p.y, m.x, m.y)
    });
  }

  private busted() {
    // Reset to Pier Head, drop wanted, lose half cash, clear pursuers
    const pier = landmark('Pier Head');
    if (this.player.mode === 'InVehicle') this.player.exitVehicle();
    this.player.sprite.setPosition(pier.x, pier.y);
    this.player.hp = PLAYER.health;
    this.player.cash = Math.floor(this.player.cash / 2);
    this.wanted.stars = 0;
    this.lastCopSpawn = this.time.now + 5000; // 5s grace before fresh spawns
    // Despawn every cop on foot
    for (const cop of this.cops) cop.sprite.destroy();
    this.cops = [];
    // Despawn every cop car
    for (const cv of this.copCars) {
      cv.sprite.destroy();
      const i = this.vehicles.indexOf(cv);
      if (i >= 0) this.vehicles.splice(i, 1);
    }
    this.copCars = [];

    this.events.emit('hud:stars', 0);
    this.events.emit('hud:hp', this.player.hp, PLAYER.health);
    this.events.emit('hud:mission', { id: 'None', text: 'BUSTED. Sent back to Pier Head.' });
  }
}
